<template>
  <el-container class="app" v-if="!loading" v-loading.fullscreen.lock="working">
    <el-header
      class="header"
      id="app-header"
      role="banner"
      aria-label="App header"
    >
      <nav-main
        :desktopMenuActive="desktopMenuActive"
        :mobileMenuActive="mobileMenuActive"
        :toggleMobileNavigation="toggleMobileNavigation"
        :closeNavigation="closeNavigation"
        @setDesktopMenuActive="setDesktopMenuActive"
      ></nav-main>
      <div class="browserWarning" v-if="supportedFeaturesWarning">
        {{ $t('applicationBrowserWarning') }}
      </div>
    </el-header>
    <el-main class="main" id="app-main" role="main">
      <div class="transition--contain">
        <transition :name="transitionName">
          <router-view class="transition" />
        </transition>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { mapState } from 'vuex';
import authentication from '@/authentication';
import user from '@/components/user';
import store from './store';
import offline from '@/offline';
import NavMain from './components/navigation/NavMain.vue';
import i18n from './configured/vue-i18n';
import 'smoothscroll-polyfill';
// import OfflineIndicator from '@/components/OfflineIndicator';
import log from '@/configured/logger';
import mixins from '@/mixins';
import { createBootstrapContext } from '@/base-data/shared-load';
import { readStoredLocale } from '@/configured/locale-storage';

export default {
  name: 'App',
  mixins: [mixins.supportedFeatures],
  data() {
    return {
      loading: true,
      transitionName: 'fade',
      desktopMenuActive: false,
      mobileMenuActive: false,
    };
  },
  components: {
    NavMain,
    // OfflineIndicator,
  },
  watch: {
    $route(to, from) {
      log.info('Route changed from ' + from.path + ' to ' + to.path);
      this.$root.$emit('scrollTo', 0, 0, true, this.$route.hash, 'instant');
    },
  },
  computed: {
    ...mapState(authentication.store.namespace, ['working']),
  },
  methods: {
    closeNavigation() {
      this.desktopMenuActive = false;
      this.mobileMenuActive = false;
    },
    toggleMobileNavigation() {
      this.mobileMenuActive = !this.mobileMenuActive;
    },
    setDesktopMenuActive(active) {
      this.desktopMenuActive = active;
    },
  },
  beforeMount() {
    store
      .dispatch(`${i18n.store.namespace}/reloadLanguageMessages`)
      .then(ensureLanguage)
      .then(offline.status.beginStatusChecks)
      .then(() => {
        this.loading = false;
      });

    this.$store.dispatch(`${offline.queueStore.namespace}/runQueue`);
    this.$root.$on('scrollTo', scrollTo);
    this.$root.$on('closeNavigation', this.closeNavigation);
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split('/').length;
    const fromDepth = from.path.split('/').length;
    this.transitionName = toDepth < fromDepth ? 'slide-right' : 'slide-left';
    next();
  },
};

//////////

// In order to utilize smooth scrolling via URL hash to an element with an id
// ensure the id’d element is at a top level component
// e.g. the highest level div of a component that goes directly in {SCREEN}.vue

function scrollTo(x, y, exact, hash, behavior = 'smooth') {
  const main = document.getElementById('app-main');
  if (!main) {
    return;
  }

  const boundingBoxMain = main.getBoundingClientRect();
  let offsetX;
  let offsetY;

  if (hash) {
    const element = document.getElementById(hash.slice(1));
    if (!element) {
      return;
    }
    offsetX = element.offsetLeft - main.offsetLeft;
    offsetY = element.offsetTop - main.offsetTop;
  } else {
    offsetX = exact ? x : x + main.scrollLeft - boundingBoxMain.left;
    offsetY = exact ? y : y + main.scrollTop - boundingBoxMain.top;
  }

  if (main.scroll) {
    log.info('main.scroll', behavior);
    main.scroll({
      top: offsetY,
      left: offsetX,
      behavior: behavior,
    });
  } else if (
    typeof main.scrollTop === 'number' &&
    typeof main.scrollLeft === 'number'
  ) {
    log.info('main.scrollTop');
    main.scrollTop = offsetY;
    main.scrollLeft = offsetX;
  } else {
    log.warn('no scroll support');
  }
}

async function ensureLanguage() {
  const i18nNamespace = i18n.store.namespace;
  const initialLang = store.state[i18nNamespace].language || 'en';
  const startupContext = createBootstrapContext({ mode: 'startup' });

  try {
    const userData = await store.dispatch(
      `${user.store.namespace}/getCurrentUser`,
      { bootstrapContext: startupContext }
    );
    const pref = userData && userData.preferredLanguage;
    const trimmed = pref && String(pref).trim();
    const stored = readStoredLocale();

    let targetLang;
    if (stored) {
      targetLang = stored;
    } else if (trimmed) {
      targetLang = trimmed;
    } else {
      targetLang = initialLang;
    }

    await i18n.loadLanguageAsync(targetLang);

    if (store.state[i18nNamespace].language !== initialLang) {
      const { loadBaseDataIfNeeded } = await import('@/base-data');
      await loadBaseDataIfNeeded({
        forceInline: true,
        mode: 'language-refresh',
      });
    }
  } catch {
    const fallback = readStoredLocale() || initialLang;
    await i18n.loadLanguageAsync(fallback);
    if (store.state[i18nNamespace].language !== initialLang) {
      const { loadBaseDataIfNeeded } = await import('@/base-data');
      await loadBaseDataIfNeeded({
        forceInline: true,
        mode: 'language-refresh',
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/base/_variables.scss';
@import 'element-theme-chalk/src/mixins/mixins.scss';

.app {
  height: 100%; // Not using 100vh because of https://nicolas-hoizey.com/2015/02/viewport-height-is-taller-than-the-visible-part-of-the-document-in-some-mobile-browsers.html
}

.browserWarning {
  padding: 15px;
  font-size: 14px;
  line-height: 1.2;
  background: #fff;
}

.header {
  z-index: 1000;
  position: relative;
  height: auto !important;
  padding: 0;
  background: #ffffff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.main {
  flex: 1;
  margin: 0;
  padding: 0;
  -webkit-overflow-scrolling: touch;
  position: relative;
  overflow-x: hidden;
}
</style>
